import type { ReactNode } from "react";
import React from "react";
import { getNameColour } from "@rosette/web_components/src/user/getNameColour.js";
import type { APIPublicGamePreferences, APIUser } from "@rosette/ts/src/business/api/api_schema.js";
import Link from "next/link";
import type { LeaderboardEntry } from "@/app/(api)/api/getLeaderboard/getLeaderboard.js";
import { cn } from "@rosette/ts/src/util/cn.js";
import { Permission } from "@rosette/ts/src/business/role/Permission.js";
import { Role } from "@rosette/ts/src/business/role/Role.js";
import { Panel } from "@rosette/web_components/src/ui/Panel.js";


const LEADERBOARD = "flex flex-col overflow-hidden rounded-xl p-0 text-base text-body md:p-0";


interface LeaderboardEntryProps {
    entry: LeaderboardEntry;
    isYourEntry: boolean;
    formatValue: (
        position: number,
        user: APIUser,
        preferences: APIPublicGamePreferences,
        value: number,
    ) => ReactNode;
}


export function LeaderboardEntryElem({ entry, isYourEntry, formatValue }: LeaderboardEntryProps) {
    const { position, user, preferences, value } = entry;

    const roles = Role.list(user);
    let nameColour = preferences.nameColour;
    if (!Permission.NAME_COLOURS.hasPermission(roles)) {
        nameColour = "gray";
    }
    return (
        <div
            className={cn(
                "flex flex-row items-center gap-3 not-first:border-t not-first:border-t-panel",
                isYourEntry ? "bg-surface-sunken p-4" : "px-4 py-2",
            )}>

            <div className="min-w-[1em] font-bold">
                {`${(position + 1).toLocaleString()}.`}
            </div>
            <Link
                className="font-bold"
                href={`/u/${user.username}`}
                style={{ color: getNameColour(nameColour).colorCSS }}>

                {user.displayName}
            </Link>
            <div className="ml-auto tabular-nums">
                {formatValue(position, user, preferences, value)}
            </div>
        </div>
    );
}


interface LeaderboardProps {
    formatValue: (
        position: number,
        user: APIUser,
        preferences: APIPublicGamePreferences,
        value: number,
    ) => ReactNode;
    leaderboard: LeaderboardEntry[] | undefined;
    leaderboardYourEntry: LeaderboardEntry | null | undefined;
    leaderboardLoading: boolean;
    leaderboardError: Error | null;
}


export function Leaderboard({
    formatValue,
    leaderboard,
    leaderboardYourEntry,
    leaderboardLoading,
    leaderboardError,
}: LeaderboardProps) {

    if (!leaderboard || leaderboard.length === 0 || leaderboardLoading || leaderboardError) {
        let status = "The leaderboard is empty";
        if (leaderboardLoading) {
            status = "Loading...";
        } else if (leaderboardError) {
            console.error(leaderboardError);
            status = `Error loading leaderboard: ${leaderboardError.message}`;
        }
        return (
            <Panel tone="solid" className={LEADERBOARD}>
                <p className="p-4 m-0">
                    {status}
                </p>
            </Panel>
        );
    }

    const rows: ReactNode[] = [];
    let seenYourEntry: boolean = false;
    for (const entry of leaderboard) {
        const isYourEntry = (
            !!leaderboardYourEntry
            && leaderboardYourEntry.user.publicID === entry.user.publicID
        );
        seenYourEntry ||= isYourEntry;
        rows.push(
            <LeaderboardEntryElem
                key={entry.user.publicID}
                entry={entry}
                isYourEntry={isYourEntry}
                formatValue={formatValue} />,
        );
    }

    if (leaderboardYourEntry && !seenYourEntry) {
        rows.push(
            <LeaderboardEntryElem
                key={`your_entry`}
                entry={leaderboardYourEntry}
                isYourEntry={true}
                formatValue={formatValue} />,
        );
    }

    return (
        <Panel
            tone="solid"
            className={cn(
            LEADERBOARD,
            (!leaderboardYourEntry || leaderboardYourEntry.position !== 0) ? "pt-2 md:pt-2" : null,
        )}>
            {rows}
        </Panel>
    );
}
